import { useState } from "react";
import {
  Calendar,
  Clock,
  Pencil,
  Trash2,
  Wallet,
  Receipt,
  UserRound,
  Ban,
  Check,
  RotateCcw,
} from "lucide-react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import swal from "../../utils/swal";
import { UPLOADS_URL } from "../../config/constants";
import { useGetChildById } from "../../query/childQuery";
import { useReviewFee } from "../../query/feesQuery";
import {
  childName,
  formatAmount,
  monthYearLabel,
  statusLabel,
  voucherStatus,
  voucherTotal,
  canEditVoucher,
} from "./feeHelpers";
import FeeReceiptNo from "./FeeReceiptNo";
import { DetailCoverShowcase } from "../../components/DetailCoverHero";
import "../teachers/teacherDetails.css";
import "../activities/activities.css";
import "./fees.css";

function money(value) {
  return `PKR ${formatAmount(value)}`;
}

export default function FeeProfile({ fee, onDelete }) {
  const navigate = useNavigate();
  const childId = fee?.children?._id || fee?.child?._id || fee?.children;
  const { data: child } = useGetChildById(typeof childId === "string" ? childId : childId?._id);
  const { mutate: review, isPending } = useReviewFee();
  const [reason, setReason] = useState("");

  const student = fee?.child || child;
  const status = voucherStatus(fee);
  const editable = canEditVoucher(fee);
  const instructions = fee?.paymentInstructions || {};
  const proof = fee?.proof || {};
  const proofUrl = proof.file ? `${UPLOADS_URL}/${proof.file}` : "";
  const isPdf = String(proof.file || "").toLowerCase().endsWith(".pdf");

  const run = (action, extra) => {
    review(
      { id: fee._id, action, ...extra },
      {
        onSuccess: () => swal("Updated", "Voucher updated", "success"),
        onError: (error) => swal("Error", error?.message || "Could not update voucher", "error"),
      }
    );
  };

  const confirm = (title, text, action) => {
    swal({
      title,
      text,
      icon: "warning",
      buttons: ["Not now", "Yes"],
    }).then((ok) => {
      if (ok) run(action);
    });
  };

  return (
    <div className="td-page">
      <div className="td-main">
        <article className="td-card">
          <DetailCoverShowcase
            coverKey={fee?._id}
            backLabel="Back to fees"
            onBack={() => navigate("/fees")}
          >
            <div className="act-detail-cover-panel fee-detail-cover-panel">
              <Wallet size={48} strokeWidth={1.5} color="#b45309" />
              <p className="fee-detail-amount">{money(voucherTotal(fee))}</p>
              <div className="fee-detail-receipt-wrap">
                <FeeReceiptNo value={fee.receiptNo} size="lg" showLabel />
              </div>
              <span className={`fee-status ${status === "PAID" || status === "WAIVED" ? "is-paid" : status === "OVERDUE" || status === "REJECTED" ? "is-alert" : status === "PROOF_SUBMITTED" ? "is-wait" : "is-open"}`}>
                {statusLabel(status)}
              </span>
            </div>
          </DetailCoverShowcase>

          <div className="act-detail-body">
            <div className="act-detail-head">
              <div className="act-detail-head-copy">
                <span className="act-detail-kicker">
                  <Receipt size={14} />
                  Fee voucher
                </span>
                <div className="act-detail-title-row">
                  <h2>{childName(student)}</h2>
                  <span className="fee-period-pill">{monthYearLabel(fee.month, fee.year)}</span>
                </div>
              </div>
            </div>

            <div className="act-detail-meta">
              <span>
                <i className="is-orange">
                  <Calendar size={14} />
                </i>
                Due {fee.dueDate ? dayjs(fee.dueDate).format("MMMM D, YYYY") : "—"}
              </span>
              <span>
                <i className="is-orange">
                  <Clock size={14} />
                </i>
                Sent {fee.issuedAt ? dayjs(fee.issuedAt).format("MMMM D, YYYY") : "—"}
              </span>
            </div>

            <section className="act-detail-section">
              <h3>Amount</h3>
              <div className="fee-breakdown">
                <div>
                  <span>Fee</span>
                  <strong>{money(fee.baseAmount ?? fee.amount)}</strong>
                </div>
                {Number(fee.discountAmount) > 0 ? (
                  <div>
                    <span>Discount</span>
                    <strong>- {money(fee.discountAmount)}</strong>
                  </div>
                ) : null}
                <div>
                  <span>Late fee</span>
                  <strong>{money(fee.lateFeeAmount || 0)}</strong>
                </div>
                <div className="is-total">
                  <span>Total</span>
                  <strong>{money(voucherTotal(fee))}</strong>
                </div>
              </div>
            </section>

            <section className="act-detail-section">
              <h3>Voucher details</h3>
              <div className="fee-detail-meta-grid">
                <div className="fee-detail-meta-item">
                  <span>
                    <UserRound size={12} style={{ display: "inline", marginRight: 4 }} />
                    Student
                  </span>
                  <strong>{childName(student)}</strong>
                </div>
                <div className="fee-detail-meta-item">
                  <span>Receipt number</span>
                  <strong>
                    <FeeReceiptNo value={fee.receiptNo} />
                  </strong>
                </div>
                <div className="fee-detail-meta-item">
                  <span>Payment date</span>
                  <strong>
                    {fee.paymentDate && (status === "PAID" || status === "WAIVED")
                      ? dayjs(fee.paymentDate).format("MMMM D, YYYY")
                      : "—"}
                  </strong>
                </div>
                <div className="fee-detail-meta-item">
                  <span>Reference</span>
                  <strong>{proof.reference || "—"}</strong>
                </div>
              </div>
            </section>

            {(instructions.accountTitle || instructions.method || instructions.accountNumber || instructions.note) ? (
              <section className="act-detail-section">
                <h3>How to pay</h3>
                <div className="fee-instructions">
                  {instructions.accountTitle ? <p><span>Account</span>{instructions.accountTitle}</p> : null}
                  {instructions.method ? <p><span>Method</span>{instructions.method}</p> : null}
                  {instructions.accountNumber ? <p><span>Number</span>{instructions.accountNumber}</p> : null}
                  {instructions.note ? <p>{instructions.note}</p> : null}
                </div>
              </section>
            ) : null}

            {proof.file ? (
              <section className="act-detail-section">
                <h3>Payment proof</h3>
                {proof.note ? <p className="fee-send-lead">{proof.note}</p> : null}
                {proof.submittedAt ? (
                  <p className="fee-send-lead">Uploaded {dayjs(proof.submittedAt).format("MMMM D, YYYY h:mm A")}</p>
                ) : null}
                {isPdf ? (
                  <a className="fee-proof-link" href={proofUrl} target="_blank" rel="noreferrer">Open PDF</a>
                ) : (
                  <a href={proofUrl} target="_blank" rel="noreferrer">
                    <img className="fee-proof-image" src={proofUrl} alt="Payment proof" />
                  </a>
                )}
                {fee.rejectReason ? <p className="fee-reject">Last note: {fee.rejectReason}</p> : null}
                {status === "PROOF_SUBMITTED" ? (
                  <label className="fee-policy-note">
                    Reason if you send this back
                    <textarea
                      rows={3}
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      placeholder="The amount or date does not match this voucher."
                    />
                  </label>
                ) : null}
              </section>
            ) : null}
          </div>
        </article>

        <div className="td-actions act-detail-actions">
          {editable ? (
            <button type="button" className="td-action is-primary" onClick={() => navigate(`/fees/edit/${fee._id}`)}>
              <Pencil size={15} />
              Edit voucher
            </button>
          ) : null}
          {editable ? (
            <button type="button" className="td-action" disabled={isPending} onClick={() => confirm("Mark paid?", "The late fee is saved on the receipt and the parent is notified.", "PAID")}>
              <Check size={15} />
              Mark paid
            </button>
          ) : null}
          {status === "PROOF_SUBMITTED" ? (
            <button
              type="button"
              className="td-action"
              disabled={isPending}
              onClick={() => {
                if (!reason.trim()) {
                  swal("Add a reason", "Tell the parent why the proof was not accepted.", "info");
                  return;
                }
                run("REJECTED", { rejectReason: reason.trim() });
              }}
            >
              <RotateCcw size={15} />
              Send back
            </button>
          ) : null}
          {editable ? (
            <button type="button" className="td-action" disabled={isPending} onClick={() => confirm("Waive this fee?", "The parent is notified and nothing is collected.", "WAIVED")}>
              <Wallet size={15} />
              Waive
            </button>
          ) : null}
          {editable ? (
            <button type="button" className="td-action" disabled={isPending} onClick={() => confirm("Cancel voucher?", "You can send this month again after it is cancelled.", "CANCELLED")}>
              <Ban size={15} />
              Cancel
            </button>
          ) : null}
          {status !== "PAID" && status !== "WAIVED" ? (
            <button type="button" className="td-action is-danger" onClick={onDelete}>
              <Trash2 size={15} />
              Delete
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
